import { Spacing } from '@persona-id/design-tokens';
import { ArrowCollapseIcon, ArrowExpandIcon } from '@persona-id/design-tokens/icons';
import { forwardRef, memo, TransitionEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import HoverEditControls, {
  ControlButton,
  ControlButtonGroup,
} from 'dashboard/apps/InquiryTemplateEditor/HoverEditControls';
import { ScreenDimensions } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { PREVIEW_WIDTH } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview/InquiryTemplateScreenPreview';
import { calculateExpandableScreenFlowWidth } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/shared';
import ScreenFlowNodeWrapper from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowNodeWrapper';
import NodeSticker from 'dashboard/components/NodeSticker';
import Tooltip from 'dashboard/components/Tooltip';
import { StickerContainer } from 'dashboard/components/WorkflowStatusSticker/WorkflowStatusSticker';
import { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import Sticker from 'design-system/components/Sticker';
import DesignSystemTooltip from 'design-system/components/Tooltip';
import { Elevation } from 'design-system/variables/effect';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';
import { FRAME_HEIGHT } from 'verify/components/Application/Frame';

const HOVER_CONTROLS_MODIFIERS = [
  {
    name: 'offset',
    options: {
      offset: [0, 6],
    },
  },
  {
    name: 'flip',
    enabled: false,
  },
];

const NODE_STICKER_MODIFIERS = [
  {
    name: 'offset',
    options: {
      offset: [0, 8],
    },
  },
  {
    name: 'flip',
    enabled: false,
  },
];

// TODO (eric): replace with PDS popover when available
const NodeStickerTooltip = styled(Tooltip)<{ isTransforming: boolean }>`
  z-index: 0 !important;
  padding: 0px;
  background: none;
  ${({ isTransforming }) =>
    isTransforming &&
    css`
      visibility: hidden;
    `}
`;

// TODO (eric): replace with PDS popover when available
const HoverControlsTooltip = styled(Tooltip)<{ isTransforming: boolean }>`
  z-index: 0 !important;
  padding: 2px;
  ${({ isTransforming }) =>
    isTransforming &&
    css`
      visibility: hidden;
      ${ControlButtonGroup} {
        opacity: 0;
      }
    `}
`;

const NodeWrapper = styled.div<{
  omitFromRun?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  pointer-events: auto;
  transition: opacity ${TransitionDuration.Fast};
  opacity: ${({ omitFromRun }) => (omitFromRun ? 0.25 : 1.0)};
`;

const EXPAND_CONTAINER_LEFT = 24;
const EXPAND_CONTAINER_FLEX_GAP = 23;
const EXPAND_NODE_BASE_DELAY = 45;
export const EXPAND_COLLAPSED_CARD_FAN_WIDTH = 24;
export const EXPAND_BACKGROUND_PADDING = 12;
const EXPAND_CUBIC_BEZIER = `cubic-bezier(0.25, 0.46, 0.45, 0.94)`;

const staggeredTransitionAmount = (staggerStep: number): number => {
  return 100 + staggerStep * 20;
};

const StyledArrowExpandIcon = styled(ArrowExpandIcon)`
  transform: rotate(45deg);
`;

const StyledArrowCollapseIcon = styled(ArrowCollapseIcon)`
  transform: rotate(45deg);
`;

const ExpandableContainer = styled.div<{
  isStackVisible?: boolean;
  isStackExpanded?: boolean;
  expandNoteCount: number;
  screenWidth: number;
}>`
  display: flex;
  position: absolute;
  top: 0;
  left: calc(100% + ${EXPAND_CONTAINER_LEFT}px);
  gap: ${EXPAND_CONTAINER_FLEX_GAP}px;
  z-index: ${({ isStackVisible }) => (isStackVisible ? 2 : 1)};
  width: ${({ isStackExpanded, expandNoteCount, screenWidth }) =>
    isStackExpanded
      ? `${(expandNoteCount - 1) * EXPAND_CONTAINER_FLEX_GAP + expandNoteCount * screenWidth}px`
      : '0px'};
`;

const BaseExpandNode = styled.div<{
  isStackExpanded?: boolean;
  stackLength?: number;
}>`
  position: relative;
  display: flex;
  z-index: 3;

  ${({ isStackExpanded, stackLength, theme }) =>
    `
      position: relative;
      display: flex;

      &:before,
      &:after {
        transition: left 100ms ease-out;
        content: '';
        display: block;
        position: absolute;
        width: 100%;
        height: 100%;
        background: ${theme.color.primary.white};
        z-index: -1;
        border: 1px solid #E6E6EA;
        border-radius: ${theme.borderRadius};
        top: 0;
      }

      &:before {
        transform: scale(1, .9);
        transition-delay: ${
          isStackExpanded
            ? `25ms`
            : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              `${(stackLength + 1) * EXPAND_NODE_BASE_DELAY + (EXPAND_NODE_BASE_DELAY * 2 + 75)}ms`
        };
        left: ${isStackExpanded ? '0px' : '24px'};
        box-shadow: ${Elevation.High};
      }

      &:after {
        transform: scale(1, .95);
        transition-delay: ${
          isStackExpanded
            ? `75ms`
            : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              `${(stackLength + 1) * EXPAND_NODE_BASE_DELAY + (EXPAND_NODE_BASE_DELAY * 2 + 25)}ms`
        };
        left: ${isStackExpanded ? '0' : '12px'};
      }
    `}
`;

const ExpandableContainerBackground = styled.div<{
  minWidth: number;
  maxWidth: number;
  isStackExpanded: boolean;
  stackLength: number;
  selected: boolean;
  isActive: boolean;
}>`
  display: flex;
  height: 100%;
  position: absolute;
  padding: ${EXPAND_BACKGROUND_PADDING}px;
  top: -${EXPAND_BACKGROUND_PADDING + 2}px;
  left: -${EXPAND_BACKGROUND_PADDING + 2}px;
  border: 2px solid;
  border-radius: ${EXPAND_BACKGROUND_PADDING}px;
  background-color: ${({ selected, isStackExpanded, isActive, theme }) =>
    selected || isStackExpanded || isActive ? theme.color.background.secondary : 'transparent'};
  border-color: ${({ selected, isStackExpanded, isActive, theme }) =>
    selected
      ? theme.color.primary.brand600
      : isStackExpanded || isActive
        ? theme.color.primary.brand200
        : 'transparent'};
  will-change: width, border-color, background-color;
  // Note(Evan): I know transition via width is bad,
  // BUT because this element has its own stacking order,
  // there's no child or sibling elements that
  // also have to recalculate their position on reflow
  width: ${({ isStackExpanded, minWidth, maxWidth }) =>
    isStackExpanded ? `${maxWidth}px` : `${minWidth}px`};
  transition:
    ${({ stackLength }) =>
      `width ${staggeredTransitionAmount(stackLength)}ms ${EXPAND_CUBIC_BEZIER}`},
    border-color 50ms linear,
    background-color 50ms linear;
  transition-delay: ${({ stackLength }) => `${(stackLength + 1) * EXPAND_NODE_BASE_DELAY}ms`}, 0ms,
    0ms;
  box-sizing: content-box;
  z-index: 2;
`;

const ExpandNode = styled.div<{
  staggerStep: number;
  isStackExpanded: boolean;
  baseNodeSelected: boolean;
  stackLength?: number;
}>`
  will-change: transform, opacity;
  opacity: ${({ isStackExpanded }) => (isStackExpanded ? 1 : 0)};
  transition: ${({ staggerStep }) =>
    `transform ${staggeredTransitionAmount(
      staggerStep,
    )}ms ${EXPAND_CUBIC_BEZIER}, opacity ${staggeredTransitionAmount(
      staggerStep,
    )}ms ${EXPAND_CUBIC_BEZIER}`};

  transition-delay: ${({ stackLength }) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return `${(stackLength + 1) * EXPAND_NODE_BASE_DELAY}ms`;
  }};
  transform: ${({ isStackExpanded, staggerStep }) =>
    isStackExpanded
      ? `translateX(0)`
      : `translateX(calc(-${100 * (staggerStep + 1)}% - ${
          EXPAND_CONTAINER_LEFT + EXPAND_CONTAINER_FLEX_GAP * staggerStep
        }px))`};

  &:after {
    content: '';
    position: absolute;
    left: -${EXPAND_CONTAINER_FLEX_GAP}px;
    top: 0;
    bottom: 0;
    margin: auto;
    width: ${EXPAND_CONTAINER_FLEX_GAP}px;
    height: 1px;
    border-top: 1px solid;
    border-color: ${({ baseNodeSelected, theme }) =>
      baseNodeSelected ? theme.color.primary.brand600 : theme.color.primary.brand200};
    background-color: ${({ baseNodeSelected, theme }) =>
      baseNodeSelected ? theme.color.primary.brand600 : theme.color.primary.brand200};
  }
`;

const HoverControlsContainer = styled.div`
  display: flex;
  gap: ${Spacing[1]};
  align-items: center;
`;

const ExpandableButtonContainer = styled.div`
  margin-right: ${Spacing[1]};
`;

const ContentWrapper = styled.div`
  margin-top: ${Spacing[2]};
  margin-bottom: 14px;
  position: relative;
`;

const ExpandableExtendedHoverArea = styled.div<{ areaWidth: number; screenHeight: number }>`
  border-bottom: ${({ screenHeight }) => `${screenHeight / 2}px solid transparent`};
  border-left: 0px solid transparent;
  border-right: ${({ areaWidth }) => `${areaWidth}px solid transparent`};
  position: absolute;
  bottom: -${Spacing[6]};
  left: 100%;
`;

const Container = styled.div<{ shiftJustification?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: ${({ shiftJustification }) => (shiftJustification ? 'flex-start' : 'flex-end')};
  height: ${Spacing[6]};
`;

type ExpandableScreenFlowNodeWrapperProps = React.ComponentProps<typeof ScreenFlowNodeWrapper> & {
  expandNodes: React.ReactElement[];
  screenDimensions?: ScreenDimensions;
  stickerLabel?: string;
};

/**
 * This component is responsible for rendering a ExpandableScreenFlowNodeWrapper
 * which provides a name sticker and/or hover edit controls to the ITE flow editor nodes
 * as well as UI to expand/collapse stacked screens
 */
const ExpandableScreenFlowNodeWrapper = forwardRef<
  HTMLDivElement,
  ExpandableScreenFlowNodeWrapperProps
>(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  (
    {
      'data-name': dataName,
      'data-test': dataTest,
      'data-marketing-id': dataMarketingId,
      node,
      title,
      hoverEditLink,
      editable,
      selected,
      omitFromRun,
      withSticker,
      withHoverControls,
      expandNodes = [],
      screenDimensions,
      handleNodeClick,
      handleEditClick,
      handleDeleteClick,
      handleCutClick,
      handleCopyClick,
      stickerLabel,
    }: ExpandableScreenFlowNodeWrapperProps,
    forwardedRef: (instance: HTMLDivElement) => void,
  ) => {
    const [isHovered, { enable: activate, disable: deactivate }] = useToggle(false);
    const [expanded, { enable: enableExpandedStack, disable: disableExpandedStack }] =
      useToggle(false);
    const [stackVisible, setStackVisible] = useState(false);
    const [tooltipExpandedAreaWidth, setTooltipExpandedAreaWidth] = useState(0);
    const expandableBackgroundRef = useRef(null);
    const tooltipContainerRef = useRef(null);
    const shouldDisplayPopover = isHovered || selected;
    const screenWidth = screenDimensions?.width || PREVIEW_WIDTH;
    const screenHeight = screenDimensions?.height || FRAME_HEIGHT;

    let isTransforming = false;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    let zoomLevel: number | undefined = null;
    const panZoomBoxControls = usePanZoomBoxControls();
    if (panZoomBoxControls != null) {
      isTransforming = panZoomBoxControls.isTransforming;
      // we still optionally access here as we don't need zoomLevel within the InquiryRun context
      zoomLevel = panZoomBoxControls?.zoomControls?.percentage;
    }

    // expandNodes + baseNode = total screens in expandable stack
    const totalExpandableScreens = expandNodes.length + 1;

    const toggleExpandedState = useCallback(() => {
      if (expanded) {
        disableExpandedStack();
      } else {
        if (!selected) {
          // should default to the click behavior of the main node
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleNodeClick();
        }
        enableExpandedStack();
        setStackVisible(true);
      }
    }, [disableExpandedStack, enableExpandedStack, expanded, handleNodeClick, selected]);

    // collapse expanded stack on mouseleave when node is unselected
    useEffect(() => {
      if (expanded && !selected) {
        disableExpandedStack();
      }
    }, [expanded, disableExpandedStack, selected]);

    // callback to properly set the z-index of the expandable container
    useEffect(() => {
      if (expandableBackgroundRef.current == null) {
        return;
      }

      // the background has several transitions, but we only want to set
      // stacking order once the width of the background has finished transitioning
      // to its "collapsed" state
      const onBackgroundTransitionEnd = (e: TransitionEvent) => {
        if (e.propertyName === 'width' && !expanded) {
          setStackVisible(false);
        }
      };

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      expandableBackgroundRef.current.addEventListener('transitionend', onBackgroundTransitionEnd);
      return () => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        expandableBackgroundRef.current?.removeEventListener(
          'transitionend',
          onBackgroundTransitionEnd,
        );
      };
    }, [expanded, setStackVisible]);

    useEffect(() => {
      if (tooltipContainerRef.current && zoomLevel != null) {
        const scaledScreen = screenWidth * (zoomLevel / 100);
        const areaWidth =
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          Math.abs(tooltipContainerRef.current.getBoundingClientRect().width - scaledScreen) *
          (screenWidth / scaledScreen);

        if (areaWidth < 100) {
          setTooltipExpandedAreaWidth(0);
        } else {
          setTooltipExpandedAreaWidth(areaWidth);
        }
      } else {
        setTooltipExpandedAreaWidth(0);
      }
    }, [tooltipContainerRef, isHovered, zoomLevel]);

    const nodeWithHoverControls = (
      <HoverControlsTooltip
        hideArrow
        placement='bottom-start'
        tooltipShown={shouldDisplayPopover}
        isTransforming={isTransforming}
        modifiers={HOVER_CONTROLS_MODIFIERS}
        tooltip={
          withHoverControls && (
            <HoverControlsContainer ref={tooltipContainerRef}>
              {(hoverEditLink ||
                handleEditClick ||
                handleDeleteClick ||
                handleCutClick ||
                handleCopyClick) && (
                <HoverEditControls
                  hoverEditLink={hoverEditLink}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                  handleCutClick={handleCutClick}
                  handleCopyClick={handleCopyClick}
                  editable={editable}
                />
              )}
              <ExpandableButtonContainer>
                <DesignSystemTooltip
                  side='top'
                  trigger={
                    <ControlButton
                      data-test='expandable-screen-flow-node__expand-nodes-button'
                      id='expandable-screen-flow-node__expand-nodes-button'
                      onClick={toggleExpandedState}
                      aria-label={expanded ? 'Collapse' : 'Expand'}
                    >
                      {expanded ? <StyledArrowCollapseIcon /> : <StyledArrowExpandIcon />}
                    </ControlButton>
                  }
                >
                  {expanded ? 'Collapse' : 'Expand'}
                </DesignSystemTooltip>
              </ExpandableButtonContainer>
            </HoverControlsContainer>
          )
        }
      >
        <div style={{ display: 'flex', position: 'relative' }}>
          <ContentWrapper
            id={`${title}__node-wrapper`}
            data-test={dataTest}
            onClick={handleNodeClick}
          >
            <>
              <BaseExpandNode isStackExpanded={expanded} stackLength={expandNodes.length}>
                {node}
              </BaseExpandNode>
              <ExpandableContainerBackground
                ref={expandableBackgroundRef}
                isStackExpanded={expanded}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                selected={selected}
                minWidth={calculateExpandableScreenFlowWidth(screenWidth)}
                isActive={isHovered}
                maxWidth={
                  totalExpandableScreens * screenWidth +
                  expandNodes.length * EXPAND_CONTAINER_FLEX_GAP
                }
                stackLength={expandNodes.length}
              />
            </>

            <ExpandableContainer
              id='expandable-screen-flow-node__expandable-container'
              data-test='expandable-screen-flow-node__expandable-container'
              isStackVisible={stackVisible}
              isStackExpanded={expanded}
              aria-hidden={!expanded}
              expandNoteCount={expandNodes.length}
              screenWidth={screenWidth}
            >
              {expandNodes.map((expandNode, i) => (
                <ExpandNode
                  data-test='expandable-screen-flow-node__expand-node'
                  key={`${title}-expanded-${i}`}
                  staggerStep={i}
                  isStackExpanded={expanded}
                  stackLength={expandNodes.length}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  baseNodeSelected={selected}
                >
                  {expandNode}
                </ExpandNode>
              ))}
            </ExpandableContainer>
          </ContentWrapper>
          {/* Container to expand hover area between tooltip and content on the right edge of content */}
          {zoomLevel != null && isHovered && !selected && (
            <ExpandableExtendedHoverArea
              areaWidth={tooltipExpandedAreaWidth}
              screenHeight={screenHeight}
            />
          )}
        </div>
      </HoverControlsTooltip>
    );

    // Hide parentheses content if sticker label is present
    const stickerParenthesesContent = !stickerLabel
      ? `${totalExpandableScreens} screens`
      : undefined;

    return (
      <NodeWrapper
        data-name={dataName}
        data-marketing-id={dataMarketingId}
        id={`${dataMarketingId}--${dataName}`}
        onMouseEnter={activate}
        onMouseLeave={deactivate}
        ref={forwardedRef}
        omitFromRun={omitFromRun}
      >
        <Container>
          {withSticker && shouldDisplayPopover ? null : (
            <StickerContainer>
              <NodeSticker
                title={title}
                isActive={isHovered}
                selected={selected}
                parenthesesContent={stickerParenthesesContent}
              />
              {stickerLabel && <Sticker variant='basic'>{stickerLabel}</Sticker>}
            </StickerContainer>
          )}
        </Container>
        <NodeStickerTooltip
          hideArrow
          placement='top-start'
          tooltipShown={shouldDisplayPopover}
          isTransforming={isTransforming}
          modifiers={NODE_STICKER_MODIFIERS}
          tooltip={
            withSticker && (
              <StickerContainer>
                <NodeSticker
                  title={title}
                  isActive={isHovered}
                  selected={selected}
                  parenthesesContent={stickerParenthesesContent}
                />
                {stickerLabel && <Sticker variant='basic'>{stickerLabel}</Sticker>}
              </StickerContainer>
            )
          }
        >
          {nodeWithHoverControls}
        </NodeStickerTooltip>
        {/* empty bottom container used to extend hover area **/}
        <Container />
      </NodeWrapper>
    );
  },
);

ExpandableScreenFlowNodeWrapper.displayName = 'ExpandableScreenFlowNodeWrapper';

export default memo(ExpandableScreenFlowNodeWrapper);
